import type { RecentEdit } from "@api/generated/model";
import { Icon } from "@app/icons";
import { Button, Tooltip } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { type EditValueText, editNote, plainValue, RECENT_HOURS } from "./recentEdits";

type Props = {
  /** Coloured cells of the table shown: by the current user and by others. */
  counts: { mine: number; other: number };
  /** The chosen cell, when someone typed it lately: its name ("12 · Height") and its edit. */
  focused?: { name: string; edit: RecentEdit } | null;
  valueText?: EditValueText;
  /** Put the chosen cell's earlier value back; none: it cannot be (read only). */
  onPutBack?: () => void;
  /** What the put-back does, when it is not saved at once (a correction waiting for Save). */
  putBackHelp?: string;
  /** Opens every earlier value of the chosen cell (the history panel). */
  onEarlier?: () => void;
  /** Saves the undo can put back on this page, and putting the last back; none: no undo here. */
  undoable?: number;
  onUndo?: () => void;
};

/** The line above a table whose cells are typed: what the two colours mean (with how many cells each), who changed
 *  the chosen cell and a button putting its earlier value back, and one undoing the user's last save. Nothing while
 *  no cell is coloured and nothing can be undone. */
export function EditsBar({ counts, focused, valueText = plainValue, onPutBack, putBackHelp, onEarlier, undoable = 0, onUndo }: Props) {
  const { t } = useTranslation();
  if (counts.mine + counts.other === 0 && undoable === 0) return null;
  const edit = focused?.edit;
  return (
    <section className="edits-bar" aria-label={t("edits.title")}>
      <span className="grid-legend">
        <i className="edited-mine" />
        {t("edits.mineCount", { count: counts.mine })}
      </span>
      <span className="grid-legend">
        <i className="edited-other" />
        {t("edits.otherCount", { count: counts.other })}
      </span>
      <span className="mu sm">{t("edits.window", { hours: RECENT_HOURS })}</span>
      <span className="grow" />
      {focused && edit ? (
        <span className="hstack" style={{ gap: 8, flexWrap: "wrap" }} role="status">
          <span>
            <b>{focused.name}</b> · {editNote(edit, t, valueText)}
          </span>
          {onPutBack && (
            <Tooltip label={putBackHelp ?? t("edits.putBackHelp")} multiline w={280}>
              <Button size="compact-xs" variant="light" leftSection={<Icon name="undo" size={13} />} onClick={onPutBack}>
                {t("edits.putBack", { value: valueText(edit.before, edit.before_absent) })}
              </Button>
            </Tooltip>
          )}
          {onEarlier && (
            <Button size="compact-xs" variant="subtle" leftSection={<Icon name="history" size={13} />} onClick={onEarlier}>
              {t("edits.earlier")}
            </Button>
          )}
        </span>
      ) : (
        <span className="mu sm">{t("edits.hint")}</span>
      )}
      {onUndo && (
        <Tooltip label={t("edits.undoLastHelp")} multiline w={260}>
          <Button size="compact-xs" variant="default" leftSection={<Icon name="undo" size={13} />} disabled={!undoable} onClick={onUndo}>
            {t("edits.undoLast")}
          </Button>
        </Tooltip>
      )}
    </section>
  );
}
